import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, User } from "lucide-react";
import type { SalesTeamsMemberRecord } from "@graftus/schema";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useSalesTeams,
  useSalesTeamsMember,
} from "@/hooks/queries/use-sales-teams";
import { cn } from "@/lib/utils";

function formatLatestSession(
  iso: string | null | undefined,
  locale: string,
): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "—" : new Date(t).toLocaleDateString(locale);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]!.slice(0, 1)}${parts[parts.length - 1]!.slice(0, 1)}`.toUpperCase();
}

export interface MemberMentionProps {
  businessId: string;
  memberId: string;
  /** Label from the mention token or matched name; roster canonical name preferred when known. */
  displayName: string;
  roster?: ReadonlyMap<string, SalesTeamsMemberRecord> | null;
}

export function MemberMention({
  businessId,
  memberId,
  displayName,
  roster,
}: MemberMentionProps) {
  const { t, i18n } = useTranslation("intelligence");
  const dateLocale = i18n.language.startsWith("en") ? "en-GB" : "sv-SE";
  const fromRoster = roster?.get(memberId) ?? null;
  const chipLabel =
    fromRoster?.name?.trim() ||
    displayName.trim() ||
    t("memberMention.fallbackName");
  const [open, setOpen] = useState(false);

  const detailQuery = useSalesTeamsMember(
    open ? businessId : undefined,
    open ? memberId : undefined,
  );
  const teamsQuery = useSalesTeams(open ? businessId : undefined);

  const member = detailQuery.data ?? fromRoster;
  const teams = teamsQuery.data ?? [];
  const team =
    member?.salesTeamId != null
      ? teams.find((t) => t.id === member.salesTeamId)
      : null;
  const latestSessionLabel = formatLatestSession(
    member?.latestSessionAt,
    dateLocale,
  );

  const isKnownMember = Boolean(fromRoster || detailQuery.data);
  const loadingDetails = open && !fromRoster && detailQuery.isLoading;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "mx-0 inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted px-1.5 py-0.5 align-baseline text-[0.95em] font-medium leading-none text-foreground",
            "transition-colors hover:bg-muted/80",
            !isKnownMember && "opacity-90",
          )}
          aria-label={t("memberMention.ariaLabel", { name: chipLabel })}
        >
          <Avatar className="h-4 w-4 shrink-0">
            <AvatarFallback className="text-[8px] font-semibold">
              {initials(chipLabel)}
            </AvatarFallback>
          </Avatar>
          <span className="max-w-[14rem] truncate">{chipLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 text-sm">
        {loadingDetails ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            <span className="text-xs">{t("memberMention.loading")}</span>
          </div>
        ) : !member ? (
          <div className="space-y-2 text-muted-foreground">
            <div className="flex items-center gap-2 font-medium text-foreground">
              <User className="h-4 w-4 shrink-0" />
              {chipLabel}
            </div>
            <p className="text-xs">{t("memberMention.loadError")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 font-semibold leading-tight">
                <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                {member.name}
              </div>
              <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {member.title ? <div>{member.title}</div> : null}
                {team ? (
                  <div className="inline-flex items-center gap-1">
                    {team.name}
                  </div>
                ) : null}
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
              <dt className="text-muted-foreground">
                {t("memberMention.testSessions")}
              </dt>
              <dd className="text-right font-medium tabular-nums">
                {member.sessionCount}
              </dd>
              <dt className="text-muted-foreground">
                {t("memberMention.latestSession")}
              </dt>
              <dd className="text-right font-medium">{latestSessionLabel}</dd>
            </dl>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="w-full justify-center"
            >
              <Link
                to={`/coach/members/${member.id}`}
                onClick={() => setOpen(false)}
              >
                {t("memberMention.openProfile")}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
